import axios from 'axios';
import {
  AssignableGeneralPractitioner,
  DirectoryProvider,
  DirectoryProviderLookUpResponse,
  PractitionerRoleReference,
  ProviderDirectorySearchFields,
  Resource,
  ResourceQueryParams,
} from 'src/@types/crs/providerDirectory';
import { convertToQueryParams } from 'src/utils/string';
import { getRegistries } from 'src/careflow/ph-core/multi-tenant/registry/registry';
import {
  NPI_PROVIDER_IDENTIFIER_CODE,
  SSN_PROVIDER_IDENTIFIER_CODE,
} from 'src/careflow/helpers/constants';
import { Coding } from 'src/careflow/ph-core/fhir-client/resources';
import { apiClient } from 'src/careflow/ph-core/api_client/api_client';
import { PaginateQueryResponse } from 'src/api/pagination/dtos';
import { IndexedPractitioner } from './types';

const apiBase = 'crs/practitioner';

const PractitionerApi = {

  getProviderDetails: async (
    providerId: string,
    providerDirUrl?: string | null
  ): Promise<DirectoryProvider> => {
    return apiClient.get<DirectoryProvider>(
      `${apiBase}/provider/${providerId}${
        providerDirUrl ? `?providerDirUrl=${encodeURIComponent(providerDirUrl)}/provider` : ''
      }`
    );
  },

  getPractitionerRole: async (
    practitionerRole: string
  ): Promise<AssignableGeneralPractitioner | null> => {
    return apiClient.get<AssignableGeneralPractitioner>(
      `${apiBase}/practitionerRole/${practitionerRole}`
    );
  },

  getPractitionerRoles: async (
    practitionerId: string,
    pcpRoleCode: string
  ): Promise<AssignableGeneralPractitioner[] | null> => {
    return apiClient.get<AssignableGeneralPractitioner[]>(
      `${apiBase}/roles/${practitionerId}?roleCode=${pcpRoleCode}`
    );
  },

  getClassifications: async (): Promise<string[]> => {
    return apiClient.get<string[]>(
      `${apiBase}/classification/list`
    );
  },

  lookupPractitionersInDirectory: async (
    searchFields: ProviderDirectorySearchFields,
    providerDirURL?: string | null
  ): Promise<DirectoryProviderLookUpResponse> => {
    return apiClient.post<
      ProviderDirectorySearchFields & { providerDirURL?: string },
      DirectoryProviderLookUpResponse
    >(`${apiBase}/lookup`, { ...searchFields, providerDirURL: providerDirURL ?? undefined });
  },

  getValueSetFromTerminology: async (valueSetName: string, terminologyUrl: string ): Promise<any> => {
    return axios.get(`${terminologyUrl}/fhir/ValueSet/${valueSetName}`);
  },

  getResource: async (queryParams: ResourceQueryParams): Promise<Resource[]> => {
    const { search, resourceType } = queryParams;
    const url = convertToQueryParams({ search: search });
    return apiClient.get<Resource[]>(
      `${apiBase}/resources?resourceType=${resourceType}${url}`
    );
  },

  getPractitionerRolesByReference: async (
    role: string,
    practitionerId?: string,
    organizationId?: string
  ): Promise<PractitionerRoleReference[]> => {
    const practitioner = convertToQueryParams({ practitionerId: practitionerId });
    const organization = convertToQueryParams({ organizationId: organizationId });
    return apiClient.get<PractitionerRoleReference[]>(
      `${apiBase}/practitionerRoles?role=${role}${practitioner}${organization}`
    );
  },

  getPatientGeneralPractitioner: async (patientId: string): Promise<AssignableGeneralPractitioner[]> => {
    return apiClient.get<AssignableGeneralPractitioner[]>(
      `${apiBase}/patient/${patientId}/generalPractitioner`
    );
  },

  getPractitioners: async (
    query: string
  ): Promise<PaginateQueryResponse<IndexedPractitioner>> => {
    const data = await apiClient.get<PaginateQueryResponse<IndexedPractitioner>>(
      `${apiBase}?${query}`
    );
    return data;
  },

  createPractitioner: async (practitioner: any): Promise<any> => {
    return apiClient.post<any, any>(
      `${apiBase}`,
      practitioner
    );
  },

  updatePractitioner: async (practitionerId: string, practitioner: any): Promise<any> => {
    return apiClient.put<any, any>(
      `${apiBase}/${practitionerId}`,
      practitioner
    );
  }
}

export const PractitionerService = {

  getProviderDetails: async (
    providerId: string,
    providerDirUrl?: string | null
  ): Promise<DirectoryProvider | null> => {
    try {
      const data = await PractitionerApi.getProviderDetails(providerId, providerDirUrl);

      if (!data) return null;

      return {
        ...data,
        npi:
          data?.identifier?.find?.(({ code }) => code === NPI_PROVIDER_IDENTIFIER_CODE)?.value ?? '',
        ssn:
          data?.identifier?.find?.(({ code }) => code === SSN_PROVIDER_IDENTIFIER_CODE)?.value ?? '',
      };
    } catch (error) {
      return null;
    }
  },

  getPractitionerRole: async (
    practitionerRole: string
  ): Promise<AssignableGeneralPractitioner | null> => {
    const response = await PractitionerApi.getPractitionerRole(practitionerRole);
    if (!response) return null;
    return response;
  },

  getPractitionerRoles: async (
    practitionerId: string,
    pcpRoleCode: string
  ): Promise<AssignableGeneralPractitioner[] | null> => {
    const response = await PractitionerApi.getPractitionerRoles(practitionerId,pcpRoleCode);
    if (!response) return null;
    return response;
  },

  getClassifications: async (): Promise<string[]> => {
    const response = await PractitionerApi.getClassifications();
    if (!response) return [];
    return response;
  },

  lookupPractitionersInDirectory: async (
    searchFields: ProviderDirectorySearchFields,
    lookupPractitionersInDirectory?: string | null
  ): Promise<DirectoryProviderLookUpResponse | null> => {
    const response = await PractitionerApi.lookupPractitionersInDirectory(
      searchFields,
      lookupPractitionersInDirectory
    );
    if (!response) return null;
    return response;
  },

  getValueSetFromTerminology: async (
    valueSetName: string, 
    terminologyUrl: string
  ): Promise<Coding[] | null> => {
    const response = await PractitionerApi.getValueSetFromTerminology(valueSetName, terminologyUrl);
    if (!response) return null;
    return response?.data?.compose?.include?.[0]?.concept;
  },

  getProviderDirectoryMappingData: async (terminologyUrl: string) => {
    const registry = await getRegistries(undefined, 'PROVIDER-DIR-LICENSE-IDENTIFIER-TYPE-MAPPING');
    const fhirIdentifierTypes = await PractitionerApi.getValueSetFromTerminology('fhir-identifier-types', terminologyUrl);
    const licenseCodeMapping: any = JSON.parse(registry?.[0]?.keyValue);
    return {
      licenseCodeMapping,
      fhirIdentifierTypes,
    };
  },

  getResource: async (queryParams: ResourceQueryParams): Promise<Resource[]> => {
    const response = await PractitionerApi.getResource(queryParams);
    if (!response) return [];
    return response;
  },

  getPractitionerRolesByReference: async (
    role: string,
    practitionerId?: string,
    organizationId?: string
  ): Promise<PractitionerRoleReference[]> => {
    const response = await PractitionerApi.getPractitionerRolesByReference(
      role,
      practitionerId,
      organizationId
    );
    if (!response) return [];
    return response;
  },

  getPatientGeneralPractitioner: async (patientId: string): Promise<AssignableGeneralPractitioner[]> => {
    const response = await PractitionerApi.getPatientGeneralPractitioner(patientId);
    if (!response) return [];
    return response;
  },

  getPractitioners: async (
    query: string
  ): Promise<PaginateQueryResponse<IndexedPractitioner>> => {
    const response = await PractitionerApi.getPractitioners(query);
    return response;
  },

  createPractitioner: async (practitioner: any): Promise<any> => {
    const response = await PractitionerApi.createPractitioner(practitioner);
    if (!response) return null;
    return response;
  },

  updatePractitioner: async (practitionerId: string, practitioner: any): Promise<any> => {
    const response = await PractitionerApi.updatePractitioner(practitionerId, practitioner);
    if (!response) return null;
    return response;
  }
}
